/**
 * The verification beat, in three acts.
 *
 * Client ruling (Brandon, first live use): the content was right and the pacing
 * was wrong — the checks resolved in about two seconds and "we didn't even get
 * the full effect". So the send now opens on a card that is ONLY the
 * verification and works visibly through it, and the composer arrives after.
 * He asked for this explicitly as a confidence play: they hit Approve & Send
 * and they know, every time, that it went through this process.
 *
 *   ACT 1  VerificationProgressCard — the Coffman mark turning inside a
 *          progress ring, over ONE cycling phase line, resolving on "The math
 *          checks out". NO receipt list here (client, second pass: "i dont
 *          need to see the full report list on that screen").
 *   ACT 2  VerificationNotices (in the composer body, gating nothing) +
 *          VerificationAcks (in the composer body, gating Send) +
 *          VerificationSummaryDisclosure (collapsed at the bottom).
 *   ACT 3  the same progress card, ending red on the failing line. No composer.
 *
 * WHAT IS AND IS NOT STAGED. The cycling phase lines describe WHAT THE
 * VERIFICATION DOES — "Tracing every line to its source…" — and are derived
 * from the checks this run actually contains, so a first send never claims to
 * be measuring a revision. They are never verdicts. The RESOLUTION is the only
 * thing rendered from real results, and a FAILURE is not paced at all: it
 * renders the instant it is known. A ring spinning in front of a check we had
 * already failed would be the screen performing confidence it does not have.
 *
 * THE RING RUNS FROM FRAME ONE (client, 2026-08-06: "it should be the same
 * thing every time"). It used to have nothing to fill from until the preview
 * came back, so the whole request was a dot parked at 0% and riding the card's
 * rotation. A creep now covers the request and hands the staged reveal its
 * position, and the staged part is the same length on every send.
 *
 * All timing and sequencing lives in `lib/verificationReveal.ts`, pure and
 * tested, so this file can be re-skinned without anyone reasoning about clocks.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Minus, X } from 'lucide-react';
import type { SendVerification, VerificationCheckLine } from '@coffman-tm/shared';
import {
  activePhaseIndex,
  creepProgress,
  monotonicProgress,
  phasesForChecks,
  planVerificationReveal,
  revealProgress,
  type RevealPlan,
} from '../lib/verificationReveal';
import { ExpandChevron } from './design';
import coffmanIcon from '../assets/coffman-icon.png';

/**
 * Ring geometry. Sized around the MARK's natural resolution, not the other way
 * round: `coffman-icon.png` is a 45x42 raster, so the hero renders it at 1:1
 * and builds the ring out to 108px for the whitespace. Scaling a 45px source
 * up to fill a bigger ring is visibly soft, and this is the one screen the
 * client asked to be a spectacle.
 *
 * If a vector mark ever lands, this is the single place to grow: raise
 * RING_SIZE and the `.verify-mark` width together.
 */
const RING_SIZE = 108;
const RING_STROKE = 3;
const RING_R = (RING_SIZE - RING_STROKE) / 2;
const RING_C = 2 * Math.PI * RING_R;

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** The composer gates Send on these; they never hide inside the collapse. */
export function ackLines(verification: SendVerification | null): VerificationCheckLine[] {
  return (verification?.checks ?? []).filter((c) => c.status === 'ack_required' && c.ack);
}

/**
 * NON-GATING LINES THAT STILL HAVE TO BE SEEN (client ruling 2026-08-24).
 *
 * The materials enumeration lost its acknowledgment but kept its purpose: the
 * money on those lines is hand-entered off paperwork nothing in the portal
 * holds, so the one honest thing the send window can do is PRINT it where the
 * sender will read it. Folding it into the bottom collapse would have made the
 * ruling "delete the section", which it was not — Brandon kept the section and
 * removed the box.
 *
 * WHY THE RULE LIVES HERE AND NOT ON THE WIRE. Placement is the panel's
 * decision, and the other two rules (`ackLines`, `summaryLines`) are already
 * here. A `prominent` flag on the check would split one decision across the
 * API contract and the component, and would invite every future check to argue
 * for itself server-side.
 *
 * ADMITTING A SECOND MEMBER. The bar is not "important" — every check here is
 * important. It is: the check GATES NOTHING, and it carries MONEY THE PORTAL
 * CANNOT VOUCH FOR from any source of its own. That is what makes printing it
 * the only honest thing the window can do about it. A check the arithmetic
 * already proves belongs in the collapse however large its numbers are.
 */
const BODY_NOTICE_CHECKS: ReadonlySet<VerificationCheckLine['check']> = new Set([
  'materials_enumerated',
]);

/**
 * Informational lines the composer body shows in full — no checkbox, nothing
 * gated. Only when the line actually enumerates something: a week with no
 * materials says so on one line and belongs in the collapse with the rest of
 * the passed checks, not in a section of its own.
 */
export function bodyNoticeLines(verification: SendVerification | null): VerificationCheckLine[] {
  return (verification?.checks ?? []).filter(
    (c) =>
      !(c.status === 'ack_required' && c.ack) &&
      BODY_NOTICE_CHECKS.has(c.check) &&
      (c.lines?.length ?? 0) > 0,
  );
}

/**
 * Everything the body does not already show — the lines the collapse holds.
 *
 * NOT A COMPLETE VIEW OF THE RECEIPT ON ITS OWN. It subtracts the promoted
 * section, so any surface that renders this alone (a future stored-receipt
 * viewer, an export) silently drops the hand-added materials. Render it
 * alongside `bodyNoticeLines`, or read `verification.checks` directly.
 */
export function summaryLines(verification: SendVerification | null): VerificationCheckLine[] {
  const notices = new Set(bodyNoticeLines(verification));
  return (verification?.checks ?? []).filter(
    (c) => !(c.status === 'ack_required' && c.ack) && !notices.has(c),
  );
}

function StatusIcon({ status }: { status: VerificationCheckLine['status'] }) {
  if (status === 'fail') return <X size={13} strokeWidth={2.5} aria-hidden />;
  if (status === 'ack_required') return <AlertTriangle size={13} strokeWidth={2.5} aria-hidden />;
  if (status === 'not_applicable') return <Minus size={13} strokeWidth={2.5} aria-hidden />;
  return <Check size={13} strokeWidth={2.5} aria-hidden />;
}

function CheckRow({ check, animate }: { check: VerificationCheckLine; animate: boolean }) {
  return (
    <li className={`verify-row verify-${check.status}${animate ? ' verify-row-in' : ''}`}>
      <span className="verify-icon" aria-hidden>
        <StatusIcon status={check.status} />
      </span>
      <span className="verify-body">
        <span className="verify-label">{check.label}</span>
        <span className="verify-detail">{check.detail}</span>
        {check.lines && check.lines.length > 0 && (
          <ul className="verify-sublist">
            {check.lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
      </span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// ACT 1 / ACT 3 — the progress card
// ---------------------------------------------------------------------------

export function VerificationProgressCard({
  verification,
  loading,
  elapsedMs,
  onResolved,
}: {
  /** null while the preview is still in flight. */
  verification: SendVerification | null;
  loading: boolean;
  /**
   * How long the preview request took. The creep has been running for the same
   * stretch — the card mounts as the request goes out — so this is where the
   * ring stands when the reveal takes over. It no longer shortens the beat.
   */
  elapsedMs: number;
  /** Fired once, at handoff. NOT called when the send is blocked. */
  onResolved: () => void;
}) {
  const checks = useMemo(() => verification?.checks ?? [], [verification]);
  const failIndex = useMemo(() => checks.findIndex((c) => c.status === 'fail'), [checks]);
  const reduced = useMemo(() => prefersReducedMotion(), []);
  const phases = useMemo(() => phasesForChecks(checks.map((c) => c.check)), [checks]);

  const plan: RevealPlan | null = useMemo(
    () =>
      verification
        ? planVerificationReveal({
            phaseCount: phases.length,
            blocked: failIndex >= 0,
            reducedMotion: reduced,
            // Where the creep left the ring. Reduced motion never ran one.
            startProgress: reduced ? 0 : creepProgress(elapsedMs),
          })
        : null,
    [verification, phases.length, failIndex, reduced, elapsedMs],
  );

  /**
   * The clock, and the plan it was measured against, in ONE value.
   *
   * They cannot be separate state: the arrival swaps a creep clock for the
   * reveal's schedule, and for the one render between the swap and the loop's
   * next tick a creep's millisecond count read against the reveal's schedule
   * would report the beat already finished. Carrying `of` lets that render fall
   * back to t=0 — the start of the new plan — instead of mixing the two.
   *
   * `progress` rides along because it is the only number that must survive the
   * swap: it is the ratchet the monotonicity guarantee is built on.
   */
  const [clock, setClock] = useState<{ of: RevealPlan | null; t: number; progress: number }>({
    of: null,
    t: 0,
    progress: 0,
  });
  const resolvedRef = useRef(false);

  /**
   * THE HANDOFF CALLBACK IS HELD IN A REF, AND THE LOOP MUST NEVER DEPEND ON IT.
   *
   * The composer passes an inline arrow, so `onResolved` is a new function on
   * every parent render — and the parent re-renders during the beat as a matter
   * of course (the loading flag flips, the contacts fetch lands). With the
   * callback in the effect's dependencies each of those tore the loop down and
   * restarted it, which resets `started` to now: the clock jumped back toward 0
   * while the same plan was running. The creep stalled at its ratchet for the
   * length of the segment it had already run, and worse, a re-render during the
   * hold flipped `settled` back to false — the green seal and "The math checks
   * out" reverted to a running phase line in front of the PM.
   */
  const onResolvedRef = useRef(onResolved);
  useEffect(() => {
    onResolvedRef.current = onResolved;
  }, [onResolved]);

  // ONE loop drives the ring, the phase line and the handoff, so they cannot
  // disagree about how far along the beat is. It runs in two states of the same
  // clock and never in both at once: the CREEP while the preview is in flight
  // (no plan yet), then the staged reveal. The plan is a dependency, so the
  // arrival tears the creep down and starts the reveal in one go — and NOTHING
  // ELSE is, so nothing else can restart the clock mid-beat.
  useEffect(() => {
    resolvedRef.current = false;
    const resolve = () => {
      if (resolvedRef.current) return;
      resolvedRef.current = true;
      onResolvedRef.current();
    };
    // Reduced motion gets no creep. The beat collapses to zero there and the
    // ring is a static readout, which is the whole bargain.
    if (!plan && reduced) return;
    if (plan && plan.totalMs === 0) {
      setClock({ of: plan, t: Number.MAX_SAFE_INTEGER, progress: 1 });
      if (plan.handoffAtMs !== null) resolve();
      return;
    }
    const started = performance.now();
    let raf = 0;
    const tick = () => {
      const t = performance.now() - started;
      setClock((prev) => ({
        of: plan,
        t,
        progress: monotonicProgress(
          prev.progress,
          plan ? revealProgress(plan, t) : creepProgress(t),
        ),
      }));
      if (plan) {
        if (plan.handoffAtMs !== null && t >= plan.handoffAtMs) {
          resolve();
          return;
        }
        if (t >= plan.totalMs) return;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [plan, reduced]);

  // A tick measured against a plan that has since been replaced says nothing
  // about the current one — read it as the start of the new schedule.
  const now = clock.of === plan ? clock.t : 0;
  /**
   * THE SECOND RATCHET, AND IT IS NOT REDUNDANT WITH THE ONE IN THE LOOP.
   *
   * The line above deliberately reads a replaced plan's tick as t=0, which
   * covers exactly one render: the one between the verification arriving and
   * the loop's first tick against the new plan. In that render `progress` is
   * computed from `plan.startProgress`, and startProgress is `creepProgress`
   * of the PARENT's `elapsedMs` — a different clock epoch from the `started`
   * timestamp the card's own rAF measured the creep against. The two agree to
   * within the gap between the card mounting and the fetch going out, which is
   * small but not zero, and it can fall either way. Without this the ring could
   * tick backwards by that gap on the swap frame.
   *
   * `clock.progress` is the floor because it is the last value actually PAINTED.
   */
  const progress = monotonicProgress(
    clock.progress,
    plan ? revealProgress(plan, now) : reduced ? 0 : creepProgress(now),
  );
  const settled = plan ? now >= plan.resolveAtMs : false;
  const failing = failIndex >= 0 ? checks[failIndex]! : null;
  const phase = plan ? phases[activePhaseIndex(plan, now)] ?? phases[0]! : phases[0]!;
  const state = !settled || loading ? 'running' : failing ? 'fail' : 'ok';

  return (
    <div className={`verify-stage is-${state}`} role="status" aria-live="polite">
      {/* THE HERO — the Coffman mark inside a progress ring. The ring is the
          honest half: once the verification is in hand its fill is the same
          plan that paces the phase lines, so it can never report progress the
          beat has not made. Before that it shows the creep, which claims
          nothing about the server beyond "still working" — and which is also
          why the ring is never at exactly 0, where a round cap paints a bare
          dot and the ambient rotation walks it around the circle. */}
      <div className="verify-hero" aria-hidden>
        <svg className="verify-ring" width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}>
          <circle
            className="verify-ring-track"
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_R}
            fill="none"
            strokeWidth={RING_STROKE}
          />
          <circle
            className="verify-ring-fill"
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_R}
            fill="none"
            strokeWidth={RING_STROKE}
            strokeLinecap="round"
            strokeDasharray={RING_C}
            strokeDashoffset={RING_C * (1 - progress)}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          />
        </svg>
        <img src={coffmanIcon} alt="" className="verify-mark" />
        {state === 'ok' && (
          <span className="verify-seal">
            <Check size={20} strokeWidth={3} aria-hidden />
          </span>
        )}
        {state === 'fail' && (
          <span className="verify-seal is-fail">
            <X size={20} strokeWidth={3} aria-hidden />
          </span>
        )}
      </div>

      {/* ONE line at a time. While running these are phase statements — what
          the verification is doing — never verdicts. */}
      <div className="verify-phase-slot">
        {state === 'running' ? (
          <p key={phase} className="verify-phase">
            {phase}
          </p>
        ) : state === 'ok' ? (
          <p className="verify-phase is-ok">The math checks out</p>
        ) : (
          <p className="verify-phase is-fail">
            This send is blocked — {failing!.label.toLowerCase()} did not pass.
          </p>
        )}
      </div>

      {/* On a failure the actionable detail shows HERE, because the receipt
          list is gone from this act and the PM must still be told what broke. */}
      {state === 'fail' && (
        <div className="verify-stage-detail">
          <p className="verify-stage-detail-line">{failing!.detail}</p>
          {failing!.lines && failing!.lines.length > 0 && (
            <ul className="verify-sublist">
              {failing!.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          <p className="verify-stage-foot">
            Nothing was sent and the week is unchanged. Fix the problem above and try again.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ACT 2a — the acknowledgments, in the composer body (they gate Send)
// ---------------------------------------------------------------------------

export function VerificationAcks({
  verification,
  acks,
  onToggleAck,
  disabled,
}: {
  verification: SendVerification | null;
  acks: Set<string>;
  onToggleAck: (key: string, on: boolean) => void;
  disabled: boolean;
}) {
  const lines = ackLines(verification);
  if (lines.length === 0) return null;
  return (
    <div className="verify-acks">
      <div className="composer-section-label">Before this can send</div>
      {lines.map((check, i) => (
        <div key={`${check.check}-${i}`} className="verify-ack-item">
          <div className="verify-ack-detail">{check.detail}</div>
          {check.lines && check.lines.length > 0 && (
            <ul className="verify-sublist">
              {check.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          <label className="verify-ack">
            <input
              type="checkbox"
              checked={acks.has(check.ack!.key)}
              disabled={disabled}
              onChange={(e) => onToggleAck(check.ack!.key, e.target.checked)}
            />
            <span>{check.ack!.copy}</span>
          </label>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ACT 2a′ — informational sections in the body (they gate nothing)
// ---------------------------------------------------------------------------

/**
 * The same body placement the acknowledgments get, deliberately NOT their
 * visual register. Nothing here is a warning and nothing here is a question, so
 * there is no amber edge and no checkbox — it is a plain bordered section that
 * states a fact and lists the money behind it.
 *
 * The eyebrow is the check's own `label`, so the server keeps owning the copy
 * and this component keeps owning only where it goes.
 */
export function VerificationNotices({
  verification,
}: {
  verification: SendVerification | null;
}) {
  const lines = bodyNoticeLines(verification);
  if (lines.length === 0) return null;
  return (
    <div className="verify-notices">
      {lines.map((check, i) => (
        <div key={`${check.check}-${i}`} className="verify-notice-item">
          <div className="composer-section-label">{check.label}</div>
          <div className="verify-notice-detail">{check.detail}</div>
          {check.lines && check.lines.length > 0 && (
            <ul className="verify-sublist">
              {check.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ACT 2b — the collapsed receipt at the bottom of the composer
// ---------------------------------------------------------------------------

export function VerificationSummaryDisclosure({
  verification,
}: {
  verification: SendVerification | null;
}) {
  const [open, setOpen] = useState(false);
  const lines = summaryLines(verification);
  if (!verification || lines.length === 0) return null;
  return (
    <div className="verify-disclosure">
      <button
        type="button"
        className="verify-disclosure-toggle"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Check size={13} strokeWidth={2.5} aria-hidden />
        <span>The math checks out — view details</span>
        <ExpandChevron expanded={open} size={12} />
      </button>
      {open && (
        <ul className="verify-list verify-disclosure-list">
          {lines.map((check, i) => (
            <CheckRow key={`${check.check}-${i}`} check={check} animate={false} />
          ))}
        </ul>
      )}
    </div>
  );
}
